import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlanJob, type PlanJobParams } from '../hooks/usePlanJob';
import { get } from '../api';
import { getISOWeekNumber } from '../dateUtils';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Ingredient {
  name: string;
  amount: string;
}

type View = 'form' | 'running' | 'aborting' | 'done' | 'error' | 'aborted';

// ── Step Config ──

const STEP_CONFIG = [
  { num: 1, icon: '🔍', label: 'Rezepte suchen & wählen', runningTexts: ['Ich stöber durch die Rezepte...', 'Ich such was Passendes raus...', 'Kalorien im Blick, ich such weiter!'] },
  { num: 2, icon: '👩‍🍳', label: 'Rezepte erstellen', runningTexts: ['Ich rühr gerade im Topf...', 'Ich probier was Neues aus...', 'Das wird lecker, wart\'s ab!'] },
  { num: 3, icon: '⚖️', label: 'Nährwerte prüfen', runningTexts: ['Kurzer Check...', 'Sieht gut aus!'] },
  { num: 4, icon: '📋', label: 'Plan zusammenstellen', runningTexts: ['Ich puzzel die letzten Teile...', 'Fast fertig, ich sortier nur noch!'] },
  { num: 5, icon: '💡', label: 'Tipps & Zusammenfassung', runningTexts: ['Noch ein paar Tipps, dann wars das!', 'Ich fass nochmal alles zusammen...'] },
];

// ── Week Helpers (pure string math, no Date timezone issues) ──

interface WeekOption {
  weekStart: string; // YYYY-MM-DD (start day)
  label: string;     // "KW 14" or "KW 15/16"
  range: string;     // "03.04.26 – 09.04.26"
  hasPlan: boolean;
  isCurrent: boolean;
}

/** Add days to a YYYY-MM-DD string, returns YYYY-MM-DD */
function addDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().split('T')[0];
}

/** Format YYYY-MM-DD as DD.MM.YY */
function fmtDate(ymd: string): string {
  const [y, m, d] = ymd.split('-');
  return `${d}.${m}.${y.slice(2)}`;
}

const WEEKDAY_DOW: Record<string, number> = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
};

/** Get the most recent or current occurrence of `targetDay` relative to today */
function getStartDayOf(ymd: string, targetDow: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  const dow = date.getUTCDay(); // 0=Sun
  let diff = targetDow - dow;
  if (diff > 0) diff -= 7; // go back to the most recent occurrence
  return addDays(ymd, diff);
}

/** Get today as YYYY-MM-DD in household timezone */
function todayInHouseholdTz(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
}

function buildWeekOptions(existingWeekStarts: Set<string>, startDay: string): WeekOption[] {
  const todayStr = todayInHouseholdTz();
  const targetDow = WEEKDAY_DOW[startDay] ?? 1;
  const currentStart = getStartDayOf(todayStr, targetDow);
  const options: WeekOption[] = [];

  for (let i = 0; i < 4; i++) {
    const start = addDays(currentStart, i * 7);
    const end = addDays(start, 6);
    const kwStart = getISOWeekNumber(start);
    const kwEnd = getISOWeekNumber(end);
    const label = kwStart === kwEnd ? `KW ${kwStart}` : `KW ${kwStart}/${kwEnd}`;

    options.push({
      weekStart: start,
      label,
      range: `${fmtDate(start)} – ${fmtDate(end)}`,
      hasPlan: existingWeekStarts.has(start),
      isCurrent: i === 0,
    });
  }

  return options;
}

// ── Component ──

export default function PlanGeneratorSheet({ open, onClose }: Props) {
  const navigate = useNavigate();
  const job = usePlanJob();

  // Form state
  const [mealPrep, setMealPrep] = useState(false);
  const [allowRepeats, setAllowRepeats] = useState(true);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [ingName, setIngName] = useState('');
  const [ingAmount, setIngAmount] = useState('');
  const [extraNotes, setExtraNotes] = useState('');
  const [cuisinePrefs, setCuisinePrefs] = useState<string[]>([]);
  const [prepTypePrefs, setPrepTypePrefs] = useState<string[]>([]);
  const [eventTheme, setEventTheme] = useState<string | undefined>();

  // Skipped days
  const [skippedDaySet, setSkippedDaySet] = useState<Set<number>>(new Set()); // 0=Mon to 6=Sun

  // Week selection
  const [weekOptions, setWeekOptions] = useState<WeekOption[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<string>('');
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);

  // Abort pending state (while waiting for server confirmation)
  const [abortPending, setAbortPending] = useState(false);

  // Load existing plans and household settings on open
  const [planStartDay, setPlanStartDay] = useState('monday');
  const [hasCookingSchedule, setHasCookingSchedule] = useState(false);
  useEffect(() => {
    if (!open) return;
    const WEEKDAY_TO_IDX: Record<string, number> = { monday: 0, tuesday: 1, wednesday: 2, thursday: 3, friday: 4, saturday: 5, sunday: 6 };
    Promise.all([
      get<{ week_start: string }[]>('/plans'),
      get<Record<string, unknown>>('/household').catch(() => ({} as Record<string, unknown>)),
    ]).then(([plans, hhRaw]) => {
      const hh = hhRaw as Record<string, unknown>;
      const startDay = (hh.plan_start_day as string) || 'monday';
      setPlanStartDay(startDay);
      const cs = hh.cooking_schedule ? (typeof hh.cooking_schedule === 'string' ? JSON.parse(hh.cooking_schedule as string) : hh.cooking_schedule) : null;
      setHasCookingSchedule(!!(cs?.cooking_days?.length));
      const existing = new Set(plans.map(p => p.week_start));
      const options = buildWeekOptions(existing, startDay);
      setWeekOptions(options);
      const firstEmpty = options.find(o => !o.hasPlan);
      setSelectedWeek(firstEmpty?.weekStart || options[0]?.weekStart || '');
      // Load default skipped weekdays (indices relative to start day)
      const raw = hh.default_skipped_weekdays;
      if (raw) {
        const days: string[] = typeof raw === 'string' ? JSON.parse(raw) : (Array.isArray(raw) ? raw : []);
        // Convert weekday names to indices relative to start day
        const startDow = WEEKDAY_TO_IDX[startDay] ?? 0;
        const DOW_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        // Reorder DOW_ORDER starting from startDay
        const startIdx = DOW_ORDER.indexOf(startDay);
        const orderedDays = [...DOW_ORDER.slice(startIdx), ...DOW_ORDER.slice(0, startIdx)];
        setSkippedDaySet(new Set(days.map(d => orderedDays.indexOf(d)).filter(n => n >= 0)));
      }
    }).catch(() => {
      const options = buildWeekOptions(new Set(), 'monday');
      setWeekOptions(options);
      setSelectedWeek(options[0]?.weekStart || '');
    });
  }, [open]);

  if (!open) return null;

  // Determine view
  // Reset abortPending when job actually stops
  if (!job.isRunning && abortPending) {
    // Will clear on next render
    setTimeout(() => setAbortPending(false), 0);
  }

  const view: View = abortPending ? 'aborting'
    : job.status === 'aborted' ? 'aborted'
    : job.isRunning ? 'running'
    : job.status === 'completed' ? 'done'
    : (job.status === 'failed' || job.error) ? 'error'
    : 'form';

  const selected = weekOptions.find(w => w.weekStart === selectedWeek);

  const addIngredient = () => {
    if (!ingName.trim()) return;
    setIngredients(prev => [...prev, { name: ingName.trim(), amount: ingAmount.trim() }]);
    setIngName('');
    setIngAmount('');
  };

  const removeIngredient = (i: number) => {
    setIngredients(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleSubmit = async () => {
    // Check if selected week already has a plan
    if (selected?.hasPlan) {
      setShowReplaceConfirm(true);
      return;
    }
    doStartJob();
  };

  const doStartJob = async () => {
    setShowReplaceConfirm(false);
    // Compute concrete skipped dates from weekday indices
    const skippedDates: string[] = [];
    if (skippedDaySet.size > 0 && selectedWeek) {
      for (let i = 0; i < 7; i++) {
        if (skippedDaySet.has(i)) {
          skippedDates.push(addDays(selectedWeek, i));
        }
      }
    }
    const params: PlanJobParams = {
      week_start: selectedWeek,
      meal_prep: mealPrep,
      allow_repeats: allowRepeats,
      ingredients: ingredients.length > 0 ? ingredients : undefined,
      extra_notes: extraNotes.trim() || undefined,
      cuisine_preference: cuisinePrefs.length > 0 ? cuisinePrefs : undefined,
      prep_type_preference: prepTypePrefs.length > 0 ? prepTypePrefs : undefined,
      event_theme: eventTheme || undefined,
      skipped_days: skippedDates.length > 0 ? skippedDates : undefined,
    };
    await job.startJob(params);
  };

  const handleClose = () => {
    if (!job.isRunning) {
      job.reset();
    }
    setShowReplaceConfirm(false);
    onClose();
  };

  const handleViewPlan = () => {
    handleClose();
    navigate('/plan');
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[60]" onClick={view === 'form' ? onClose : undefined} />
      <div className="fixed bottom-0 left-0 right-0 md:bottom-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[440px] z-[61] flora-menu-appear">
        <div className="bg-gray-50 md:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shrink-0">
            <div className="flex items-center gap-3">
              <img src="/nico-transparent.svg" alt="Nico" className={`w-10 h-10 ${view === 'running' ? 'nico-breathe' : ''}`} />
              <span className="font-semibold text-sm">
                {view === 'running' ? 'Nico plant...' : view === 'aborting' ? 'Wird abgebrochen...' : view === 'aborted' ? 'Abgebrochen' : view === 'done' ? 'Plan erstellt!' : view === 'error' ? 'Fehler' : 'Wochenplan erstellen'}
              </span>
            </div>
            <button onClick={handleClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto px-4 py-4 space-y-4 flex-1">

            {/* ── Running: Stepper UI ── */}
            {view === 'running' && (
              <div className="space-y-3">
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-emerald-500 h-1.5 rounded-full transition-all duration-700 ease-out" style={{ width: `${job.pct}%` }} />
                </div>
                <div className="space-y-2">
                  {STEP_CONFIG.map((step) => {
                    const isDone = job.currentStep > step.num;
                    const isActive = job.currentStep === step.num;
                    const isPending = job.currentStep < step.num;
                    return (
                      <div key={step.num} className={`flex items-start gap-3 p-2.5 rounded-xl transition-all duration-300 ${isActive ? 'bg-emerald-50 border border-emerald-200' : isDone ? 'bg-gray-50' : ''}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm transition-all duration-300 ${isDone ? 'bg-emerald-500 text-white' : isActive ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-300' : 'bg-gray-200 text-gray-400'}`}>
                          {isDone ? (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                          ) : (<span>{step.icon}</span>)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${isDone ? 'text-gray-500' : isActive ? 'text-emerald-800' : 'text-gray-400'}`}>{step.label}</p>
                          {isActive && <p className="text-xs text-emerald-600 mt-0.5 animate-pulse">{job.stepLabel || step.runningTexts[0]}</p>}
                          {isPending && <p className="text-[11px] text-gray-300">Wartet...</p>}
                        </div>
                        {isActive && <div className="w-5 h-5 border-2 border-emerald-200 border-t-emerald-500 rounded-full animate-spin shrink-0 mt-0.5" />}
                      </div>
                    );
                  })}
                </div>
                {job.recipes.length > 0 && (
                  <div className="bg-white rounded-xl p-3 border border-gray-100">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Neue Rezepte</p>
                    <div className="space-y-1">
                      {job.recipes.map((r, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.status === 'created' ? 'bg-emerald-500' : 'bg-amber-400 animate-pulse'}`} />
                          <span className={r.status === 'created' ? 'text-gray-700' : 'text-gray-400'}>{r.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <button onClick={() => { setAbortPending(true); job.abortJob(); }} className="w-full py-2.5 text-sm text-gray-400 hover:text-red-500 transition-colors">Abbrechen</button>
              </div>
            )}

            {/* ── Done: Summary Card ── */}
            {view === 'done' && job.summary && (
              <div className="text-center py-2">
                <div className="w-14 h-14 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                  <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                </div>
                <p className="text-base font-semibold text-gray-800 mb-4">Wochenplan erstellt!</p>
                <div className="bg-white rounded-xl p-4 border border-gray-100 text-left space-y-2.5">
                  <StatRow label="Tage geplant" value={String(job.summary.days_planned)} />
                  <StatRow label="Rezepte gesamt" value={String(job.summary.total_recipes)} sub={job.summary.new_recipes > 0 ? `${job.summary.new_recipes} neu erstellt` : undefined} />
                  <StatRow label="Ø Kalorien / Tag" value={`${job.summary.avg_kcal_per_day} kcal`} />
                  <StatRow label="Ø Protein / Tag" value={`${job.summary.avg_protein_per_day}g`} />
                  {job.summary.highlight && (
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs text-emerald-600 font-medium">{job.summary.highlight}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {view === 'done' && job.needsFollowUpPlan && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mt-3">
                <p className="text-xs font-medium text-blue-800">Dein Kochtag-Zyklus reicht in die nächste Woche.</p>
                <p className="text-[10px] text-blue-600 mt-0.5">Generiere den Folgeplan, damit du frühzeitig weißt was eingekauft werden muss.</p>
              </div>
            )}

            {/* ── Done without summary ── */}
            {view === 'done' && !job.summary && (
              <div className="text-center py-6">
                <div className="w-12 h-12 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                </div>
                <p className="text-sm font-medium text-gray-700">Wochenplan wurde gespeichert!</p>
              </div>
            )}

            {/* ── Error State ── */}
            {view === 'error' && (
              <div className="text-center py-4">
                <div className="w-12 h-12 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{job.error || 'Plan-Generierung fehlgeschlagen'}</div>
                {job.errorStep && <p className="text-xs text-gray-400 mt-2">Fehler bei Schritt {job.errorStep}: {STEP_CONFIG[job.errorStep - 1]?.label}</p>}
                <button onClick={() => job.reset()} className="mt-3 text-sm text-emerald-600 font-medium hover:text-emerald-700">Erneut versuchen</button>
              </div>
            )}

            {/* ── Aborting State ── */}
            {view === 'aborting' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4">
                  <img src="/nico-transparent.svg" alt="Nico" className="w-full h-full opacity-50" style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
                </div>
                <p className="text-sm font-medium text-gray-600">Ich höre auf, Moment noch...</p>
                <p className="text-xs text-gray-400 mt-1">Der aktuelle Schritt wird noch abgeschlossen.</p>
                <div className="w-6 h-6 mx-auto mt-4 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin" />
              </div>
            )}

            {/* ── Aborted State ── */}
            {view === 'aborted' && (
              <div className="text-center py-6">
                <div className="w-14 h-14 mx-auto mb-3">
                  <img src="/nico-transparent.svg" alt="Nico" className="w-full h-full opacity-40" />
                </div>
                <p className="text-sm font-medium text-gray-700">Plan-Erstellung abgebrochen</p>
                <p className="text-xs text-gray-400 mt-1">Kein Problem! Du kannst jederzeit einen neuen Plan starten.</p>
                <button onClick={() => { setAbortPending(false); job.reset(); }} className="mt-4 text-sm text-emerald-600 font-medium hover:text-emerald-700">
                  Neuen Plan erstellen
                </button>
              </div>
            )}

            {/* ── Form State ── */}
            {view === 'form' && (
              <>
                {/* Week Selector */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Woche auswählen</label>
                  <div className="mt-1.5 space-y-1.5">
                    {weekOptions.map(w => (
                      <button
                        key={w.weekStart}
                        onClick={() => setSelectedWeek(w.weekStart)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all ${
                          selectedWeek === w.weekStart
                            ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-300'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        {/* Radio dot */}
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          selectedWeek === w.weekStart ? 'border-emerald-500' : 'border-gray-300'
                        }`}>
                          {selectedWeek === w.weekStart && <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />}
                        </div>

                        {/* Week info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-800">{w.label}</span>
                            {w.isCurrent && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-medium">Aktuell</span>}
                          </div>
                          <p className="text-[12px] text-gray-400 mt-0.5">{w.range}</p>
                          {w.hasPlan ? (
                            <p className="text-[11px] text-amber-600 font-medium mt-0.5 flex items-center gap-1">
                              <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
                              Plan vorhanden — wird ersetzt
                            </p>
                          ) : (
                            <p className="text-[11px] text-emerald-600 mt-0.5 flex items-center gap-1">
                              <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                              Kein Plan — wird neu erstellt
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Skip Days */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tage planen</label>
                  <p className="text-[11px] text-gray-400 mb-1.5">Tage abwählen, die nicht geplant werden sollen</p>
                  <div className="flex gap-1.5">
                    {(() => {
                      const ALL = [
                        { key: 'monday', label: 'Mo' }, { key: 'tuesday', label: 'Di' }, { key: 'wednesday', label: 'Mi' },
                        { key: 'thursday', label: 'Do' }, { key: 'friday', label: 'Fr' }, { key: 'saturday', label: 'Sa' }, { key: 'sunday', label: 'So' },
                      ];
                      const si = ALL.findIndex(d => d.key === planStartDay);
                      return [...ALL.slice(si), ...ALL.slice(0, si)];
                    })().map((d, idx) => {
                      const label = d.label;
                      const isSkipped = skippedDaySet.has(idx);
                      return (
                        <button key={idx}
                          onClick={() => setSkippedDaySet(prev => {
                            const next = new Set(prev);
                            if (next.has(idx)) next.delete(idx);
                            else next.add(idx);
                            return next;
                          })}
                          className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                            isSkipped
                              ? 'bg-gray-200 text-gray-400 line-through'
                              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          }`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Toggles */}
                <div className="space-y-3">
                  {!hasCookingSchedule && (
                    <Toggle label="Meal-Prep / Vorkochen" description="Plane so, dass ich doppelte Mengen kochen und Zutaten wiederverwenden kann" checked={mealPrep} onChange={setMealPrep} />
                  )}
                  <Toggle label="Rezepte wiederholen erlaubt" description="Bewährte Rezepte dürfen mehrfach in der Woche vorkommen" checked={allowRepeats} onChange={setAllowRepeats} />
                </div>

                {/* Ingredients list */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Zutaten die ich zuhause habe</label>
                  <p className="text-[11px] text-gray-400 mb-2">Nico erstellt Rezepte daraus und plant sie ein</p>
                  {ingredients.length > 0 && (
                    <div className="space-y-1.5 mb-2">
                      {ingredients.map((ing, i) => (
                        <div key={i} className="flex items-center gap-2 bg-gray-200 rounded-lg px-3 py-1.5">
                          <span className="text-sm flex-1">{ing.name}{ing.amount ? <span className="text-gray-500 ml-1">({ing.amount})</span> : null}</span>
                          <button onClick={() => removeIngredient(i)} className="text-gray-400 hover:text-red-500 transition-colors">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input type="text" value={ingName} onChange={e => setIngName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addIngredient(); } }}
                      placeholder="z.B. Brokkoli" className="flex-1 px-3 py-2 bg-gray-200 rounded-lg text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-emerald-500" />
                    <input type="text" value={ingAmount} onChange={e => setIngAmount(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addIngredient(); } }}
                      placeholder="500g" className="w-20 px-3 py-2 bg-gray-200 rounded-lg text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-emerald-500" />
                    <button onClick={addIngredient} disabled={!ingName.trim()} className="px-3 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium disabled:opacity-30 transition-opacity">+</button>
                  </div>
                </div>

                {/* Cuisine preference (optional) */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Küchen diese Woche (optional)</label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {['Asiatisch', 'Indisch', 'Italienisch', 'Mediterran', 'Mexikanisch', 'Orientalisch', 'Deutsch', 'Afrikanisch'].map(c => {
                      const val = c.toLowerCase();
                      const active = cuisinePrefs.includes(val);
                      return (
                        <button key={val} onClick={() => setCuisinePrefs(prev => active ? prev.filter(x => x !== val) : [...prev, val])}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${active ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Prep type preference (optional) */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Zubereitungsarten (optional)</label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {[['1-Pot', '1pot'], ['Backofen', 'backofen'], ['Pfanne', 'pfanne'], ['Bowl', 'bowl'], ['Wrap', 'wrap'], ['Suppe', 'suppe'], ['Salat', 'salat'], ['Smoothie', 'smoothie']].map(([label, val]) => {
                      const active = prepTypePrefs.includes(val);
                      return (
                        <button key={val} onClick={() => setPrepTypePrefs(prev => active ? prev.filter(x => x !== val) : [...prev, val])}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${active ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Event theme (optional) */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Anlass (optional)</label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {[['Weihnachten', 'weihnachten'], ['Ostern', 'ostern'], ['Silvester', 'silvester'], ['Grillen', 'grillen'], ['Geburtstag', 'geburtstag'], ['Brunch', 'brunch']].map(([label, val]) => {
                      const active = eventTheme === val;
                      return (
                        <button key={val} onClick={() => setEventTheme(active ? undefined : val)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${active ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Extra notes */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Zusätzliche Wünsche</label>
                  <textarea value={extraNotes} onChange={e => setExtraNotes(e.target.value)}
                    placeholder="z.B. Mittwoch abend bin ich nicht da, Freitag soll schnell gehen..."
                    rows={2} className="w-full mt-1 px-3 py-2 bg-gray-200 rounded-lg text-sm placeholder-gray-400 outline-none resize-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </>
            )}
          </div>

          {/* Footer buttons */}
          {view === 'form' && (
            <div className="px-4 py-3 border-t border-gray-200 shrink-0" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
              <button onClick={handleSubmit} className="w-full py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 active:bg-emerald-700 transition-colors">
                Plan erstellen lassen
              </button>
            </div>
          )}

          {view === 'done' && (
            <div className="px-4 py-3 border-t border-gray-200 shrink-0 space-y-2" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
              {job.needsFollowUpPlan && job.followUpWeekStart && (
                <button
                  onClick={async () => {
                    job.reset();
                    const followUpParams: PlanJobParams = {
                      week_start: job.followUpWeekStart!,
                    };
                    await job.startJob(followUpParams);
                  }}
                  className="w-full py-2.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl font-medium text-sm hover:bg-blue-100 transition-colors">
                  Folgeplan generieren (ab {job.followUpWeekStart})
                </button>
              )}
              <div className="flex gap-2">
                <button onClick={handleClose} className="flex-1 py-3 bg-gray-100 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-colors">Schließen</button>
                <button onClick={handleViewPlan} className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 transition-colors">Plan ansehen</button>
              </div>
            </div>
          )}

          {view === 'error' && (
            <div className="px-4 py-3 border-t border-gray-200 shrink-0" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
              <button onClick={handleClose} className="w-full py-3 bg-gray-100 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-colors">Schließen</button>
            </div>
          )}

          {view === 'aborted' && (
            <div className="px-4 py-3 border-t border-gray-200 shrink-0" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
              <button onClick={handleClose} className="w-full py-3 bg-gray-100 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-colors">Schließen</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Replace Confirmation Dialog ── */}
      {showReplaceConfirm && selected && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[70]" onClick={() => setShowReplaceConfirm(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] z-[71]">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="px-5 pt-5 pb-3 text-center">
                <div className="w-12 h-12 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-gray-800 mb-1">Plan ersetzen?</p>
                <p className="text-[13px] text-gray-500 leading-snug">
                  Für <strong>{selected.label}</strong> ({selected.range}) existiert bereits ein Plan. Der bestehende Plan wird dabei <strong>ersetzt</strong>.
                </p>
              </div>
              <div className="px-5 pb-5 flex gap-2">
                <button onClick={() => setShowReplaceConfirm(false)} className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors">
                  Abbrechen
                </button>
                <button onClick={doStartJob} className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-medium hover:bg-amber-600 transition-colors">
                  Ersetzen
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ── Toggle Component ──

function Toggle({ label, description, checked, onChange }: {
  label: string; description: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <button onClick={() => onChange(!checked)} className="w-full flex items-start gap-3 text-left">
      <div className={`mt-0.5 w-10 h-6 rounded-full transition-colors shrink-0 relative ${checked ? 'bg-emerald-500' : 'bg-gray-300'}`}>
        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="text-[11px] text-gray-400 leading-tight">{description}</p>
      </div>
    </button>
  );
}

// ── Stat Row Component ──

function StatRow({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-sm text-gray-500">{label}</span>
      <div className="text-right">
        <span className="text-sm font-semibold text-gray-800">{value}</span>
        {sub && <p className="text-[11px] text-emerald-600">{sub}</p>}
      </div>
    </div>
  );
}
